<template>
  <div class="slider-container">
    <v-responsive aspect-ratio="2.94" class="rounded-xl overflow-hidden skeleton-wrapper" v-if="loading">
      <v-skeleton-loader type="image" class="fill-height custom-skeleton"></v-skeleton-loader>
    </v-responsive>

    <v-carousel
      v-model="activeIndex"
      v-else-if="slides && slides.length > 0"
      :show-arrows="false"
      hide-delimiter-background
      delimiter-icon="mdi-minus"
      :hide-delimiters="slides.length <= 1"
      cycle
      height="auto"
      class="rounded-xl main-slider"
      interval="6000">
      <v-carousel-item v-for="(slide, i) of slides" :key="slideKey(slide)" @click="handleClick(slide)" class="slider-item">
        <v-img
          contain
          :eager="i === activeIndex"
          :transition="false"
          :src="'/img/' + slide.image"
          aspect-ratio="2.94"
          class="slider-img"
          gradient="to bottom, rgba(0,0,0,0) 40%, rgba(0,0,0,0.2) 70%, rgba(0,0,0,0.9) 100%">
          <div class="slider-content pa-4">
            <v-chip v-if="slide.isParty" color="red accent-3" text-color="white" small class="font-weight-black pulse-animation mb-1">
              EVENT 🔥
            </v-chip>

          </div>
        </v-img>
      </v-carousel-item>
    </v-carousel>

    <party-overlay v-model="showPartyOverlay" :party="selectedParty" :user="user"></party-overlay>
  </div>
</template>

<script>
import api from "@/api"
import socketService from "@/socketService"
import PartyOverlay from "@/components/home/partyOverlay.vue"
import { mapState } from "vuex"
import userService from "@/services/userService"
import { copySliderGroups, visibleSliderGroups, sliderDay } from "@/store/homeSliderStore"

export default {
  name: "HomeTopSlider",
  components: { PartyOverlay },
  data() {
    return {
      loading: true,
      slides: [],
      groups: { parties: [], events: [], main: [] },
      activeIndex: 0,
      scope: null,
      loadId: 0,
      hasSnapshot: false,
      refreshing: false,
      pendingSlides: [],
      slideHandler: null,
      showPartyOverlay: false,
      selectedParty: null,
      user: JSON.parse(localStorage.getItem("user") || "{}"),
    }
  },
  computed: {
    ...mapState("studioStore", ["selectedStudio"]),
  },
  watch: {
    "selectedStudio._id": "restoreAndRefresh",
    "$store.state.userStore.user._id": "restoreAndRefresh",
    activeIndex() {
      if (this.scope && this.scope === this.getScope()) {
        this.$store.commit("homeSliderStore/select", { scope: this.scope, activeId: this.slideKey(this.slides[this.activeIndex]) })
      }
    },
  },
  async created() {
    this.setupSocketListeners()
    await this.restoreAndRefresh()
  },
  beforeDestroy() {
    this.loadId += 1
    this.saveSnapshot()
    socketService.off("slide_changed", this.slideHandler)
    socketService.off("slide_created", this.slideHandler)
  },
  methods: {
    async restoreAndRefresh() {
      const scope = this.getScope()
      if (scope === this.scope && this.hasSnapshot) return
      this.scope = scope
      this.hasSnapshot = false
      this.loading = true
      this.groups = { parties: [], events: [], main: [] }
      this.slides = []
      this.$store.commit("homeSliderStore/restore", { scope })
      const cached = this.$store.state.homeSliderStore.snapshot
      if (scope && cached?.scope === scope) {
        this.hasSnapshot = true
        this.applyGroups(cached.groups, cached.activeId)
        this.loading = false
      }
      await this.loadSliderContent()
    },
    getScope() {
      const userId = userService.getUserId()
      const studioId = this.selectedStudio?._id
      if (!userId || !studioId) return null
      return JSON.stringify([userId, studioId, userService.hasRight("c_create"), sliderDay()])
    },
    slideKey(slide) {
      return slide ? `${slide.sliderType}:${slide._id || slide.image}` : null
    },
    applyGroups(groups, activeId = this.slideKey(this.slides[this.activeIndex])) {
      this.groups = visibleSliderGroups(groups, this.selectedStudio?._id, userService.hasRight("c_create"))
      this.slides = [
        ...this.groups.parties.map((slide) => ({ ...slide, sliderType: "party", isParty: true })),
        ...this.groups.events.map((slide) => ({ ...slide, sliderType: "event", isParty: false })),
        ...this.groups.main.map((slide) => ({ ...slide, sliderType: "main", isParty: false })),
      ]
      const index = this.slides.findIndex((slide) => this.slideKey(slide) === activeId)
      this.activeIndex = index < 0 ? 0 : index
    },
    saveSnapshot() {
      if (!this.hasSnapshot || !this.scope || this.scope !== this.getScope()) return
      // Include changes made by the party signup overlay, without sharing store objects with it.
      const groups = {
        parties: this.slides.filter((slide) => slide.sliderType === "party"),
        events: this.slides.filter((slide) => slide.sliderType === "event"),
        main: this.slides.filter((slide) => slide.sliderType === "main"),
      }
      this.$store.commit("homeSliderStore/save", {
        scope: this.scope,
        groups,
        activeId: this.slideKey(this.slides[this.activeIndex]),
      })
    },
    mergeMainSlide(groups, slide) {
      const index = groups.main.findIndex((entry) => entry._id === slide._id)
      if (index >= 0) groups.main.splice(index, 1, slide)
      else groups.main.push(slide)
    },
    async loadSliderContent() {
      const loadId = ++this.loadId
      const scope = this.scope
      const token = localStorage.getItem("token")
      this.refreshing = true
      this.pendingSlides = []
      try {
        const results = await Promise.allSettled([api.fetchParties(), api.fetchEventsForSlider(), api.fetchMainSlider()])
        if (loadId !== this.loadId || scope !== this.getScope() || token !== localStorage.getItem("token")) return
        const groups = copySliderGroups(this.groups)
        const sources = ["parties", "events", "main"]
        results.forEach((result, index) => {
          if (result.status === "fulfilled" && Array.isArray(result.value)) {
            groups[sources[index]] = result.value
            this.hasSnapshot = true
          } else {
            if ([401, 403].includes(result.reason?.response?.status)) groups[sources[index]] = []
            console.error(`Slider ${sources[index]} konnte nicht geladen werden`, result.reason)
          }
        })
        // Socket updates received during the fetch are newer than the request snapshot.
        this.pendingSlides.forEach((slide) => this.mergeMainSlide(groups, slide))
        this.applyGroups(groups)
        this.saveSnapshot()
      } finally {
        if (loadId === this.loadId) {
          this.refreshing = false
          this.pendingSlides = []
          this.loading = false
        }
      }
    },

    handleClick(slide) {
      if (slide.isParty) {
        this.selectedParty = slide
        this.showPartyOverlay = true
      } else {
        const link = slide.link
        if (link) {
          if (link.includes("http")) {
            window.open(link, "_blank").focus()
          } else {
            this.$router.push(link)
          }
        }
      }
    },

    setupSocketListeners() {
      this.slideHandler = (data) => {
        if (data.slide) this.handleSlideUpdate(data.slide)
      }
      socketService.on("slide_changed", this.slideHandler)
      socketService.on("slide_created", this.slideHandler)
    },

    handleSlideUpdate(slide) {
      if (!this.scope || this.scope !== this.getScope()) return
      this.hasSnapshot = true
      if (this.refreshing) this.pendingSlides.push(slide)
      const groups = copySliderGroups(this.groups)
      this.mergeMainSlide(groups, slide)
      this.applyGroups(groups)
      this.saveSnapshot()
    },
  },
}
</script>

<style lang="scss" scoped>
/* Container Styles */
.slider-container {
  position: relative;
  width: 100%;
}

/* Slider Shadow & Radius */
.main-slider {
  box-shadow: 0 10px 30px -10px rgba(0, 0, 0, 0.6) !important;
  transition: transform 0.2s;

  &:active {
    transform: scale(0.98);
  }
}

/* Skeleton Dark Mode Fix */
.custom-skeleton {
  background: rgba(255, 255, 255, 0.05) !important;
}
::v-deep .v-skeleton-loader__image {
  height: 100%;
  background: rgba(255, 255, 255, 0.05) !important;
}

/* Content Overlay Positioning */
.slider-content {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  z-index: 2;
}

.text-shadow {
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.8);
}

/* Animations */
.pulse-animation {
  animation: pulse-red 2s infinite;
  box-shadow: 0 0 0 0 rgba(255, 23, 68, 0.7);
}

@keyframes pulse-red {
  0% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(255, 23, 68, 0.7);
  }
  70% {
    transform: scale(1);
    box-shadow: 0 0 0 10px rgba(255, 23, 68, 0);
  }
  100% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(255, 23, 68, 0);
  }
}

/* Vuetify Overrides für cleaneren Look */
::v-deep .v-carousel__controls {
  background: transparent;
  padding-bottom: 8px;

  .v-btn--active {
    color: white !important;
    transform: scale(1.2);
  }
  .v-btn {
    color: rgba(255, 255, 255, 0.5) !important;
  }
}
</style>
